import { Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { ShieldCheck, TrendingUp, Wrench, BadgeCheck, Landmark, Calendar } from "lucide-react";

const CATEGORIES = [
    { name: "Waterjet", desc: "Hydrodynamic weaving systems", count: "320+ Members" },
    { name: "Airjet", desc: "High-speed pneumatic looms", count: "280+ Members" },
    { name: "Rapier", desc: "Versatile gripper weaving", count: "410+ Members" },
    { name: "Spare Parts", desc: "Components & consumables", count: "550+ Suppliers" },
    { name: "Technical Services", desc: "Installation, calibration, support", count: "180+ Providers" },
];

const STATS = [
    { value: "1,847", label: "Registered Members" },
    { value: "₹12.4Cr", label: "Annual Industry Trade" },
    { value: "39", label: "Years of Service" },
    { value: "24", label: "Annual Events Hosted" },
];

const WHY_JOIN = [
    { icon: ShieldCheck, title: "Industry Recognition", desc: "Become part of a verified network of trusted textile machinery suppliers and service providers." },
    { icon: TrendingUp, title: "Business Growth", desc: "Expand your business through exhibitions, networking events, and member referrals." },
    { icon: Wrench, title: "Technical Collaboration", desc: "Access industry experts, technical workshops, and knowledge-sharing platforms." },
    { icon: BadgeCheck, title: "Member Verification", desc: "Receive an official STMF Membership ID and Digital Verification Certificate." },
    { icon: Landmark, title: "Representation", desc: "Benefit from collective representation before government bodies, financial institutions, and industry organizations." },
    { icon: Calendar, title: "Exhibition Opportunities", desc: "Participate in STMF-organized exhibitions and trade events." },
];

const MISSION = [
    "Promote ethical and responsible business practices.",
    "Support genuine machinery suppliers and service providers.",
    "Enhance cooperation among industry stakeholders.",
    "Facilitate technical knowledge sharing and skill development.",
    "Organize exhibitions, seminars, and networking opportunities.",
    "Represent member interests before government authorities and financial institutions.",
];

export default function Landing() {
    return (
        <div className="min-h-screen flex flex-col">
            <PublicHeader />

            {/* HERO */}
            <section data-testid="hero-section" className="relative overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/8246480/pexels-photo-8246480.jpeg)" }} />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 lg:py-40">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.25em] mb-6 fade-in-up">
                            <span className="block w-8 h-px bg-[#D4AF37]" />
                            Trusted Dealers · Reliable Technology · Strong Industry
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6 fade-in-up" style={{ animationDelay: "0.1s" }}>
                            Strengthening the textile machinery industry through <span className="text-[#D4AF37]">unity, integrity &amp; innovation</span>.
                        </h1>
                        <p className="text-lg text-white/80 max-w-2xl mb-10 leading-relaxed fade-in-up" style={{ animationDelay: "0.2s" }}>
                            A leading industry body representing dealers, importers, suppliers, service providers and technical experts engaged in imported textile machinery, spare parts, installation, maintenance and technical support services.
                        </p>
                        <div className="flex flex-wrap gap-4 fade-in-up" style={{ animationDelay: "0.3s" }}>
                            <Link to="/apply" data-testid="hero-cta-apply" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D4AF37] text-[#0F172A] text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#B4952D] transition-colors">
                                Apply for Membership <span>→</span>
                            </Link>
                            <Link to="/events" data-testid="hero-cta-events" className="inline-flex items-center gap-2 px-7 py-4 border border-white/30 text-white text-sm font-semibold uppercase tracking-[0.08em] hover:bg-white/10 transition-colors">
                                View Upcoming Events
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS STRIP */}
            <section data-testid="stats-strip" className="border-y border-[#E2E8F0] bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E2E8F0]">
                    {STATS.map((s, i) => (
                        <div key={s.label} className="px-6 py-10 text-center first:pl-0" data-testid={`stat-${i}`}>
                            <div className="font-display text-3xl lg:text-5xl font-bold text-[#0F172A] tabular">{s.value}</div>
                            <div className="text-xs uppercase tracking-[0.15em] text-[#64748B] mt-2">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* WELCOME / VISION */}
            <section data-testid="welcome-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Welcome to STMF</div>
                    <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#0F172A] leading-tight">
                        A common platform for industry professionals.
                    </h2>
                </div>
                <div className="md:col-span-7 space-y-5 text-[#64748B] leading-relaxed">
                    <p>STMF Association (Surat Texmac Federation) is a leading industry body representing dealers, importers, suppliers, service providers, and technical experts engaged in imported textile machinery, spare parts, installation, maintenance, and technical support services.</p>
                    <p>The Association serves as a common platform for industry professionals dealing in Waterjet Looms, Airjet Looms, Rapier Looms, textile machinery components, and related services.</p>
                    <p className="text-[#0F172A] font-medium">STMF is committed to promoting ethical business practices, protecting member interests, supporting textile manufacturers, and driving sustainable industry growth.</p>
                </div>
            </section>

            {/* VISION + MISSION */}
            <section className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-12">
                    <div data-testid="vision-block" className="bg-white border border-[#E2E8F0] p-10">
                        <div className="font-mono text-xs text-[#D4AF37] mb-3">01 · OUR VISION</div>
                        <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-4 leading-snug">
                            The most trusted and recognized association for the textile machinery industry.
                        </h3>
                        <p className="text-[#64748B] leading-relaxed">
                            We foster transparency, professionalism, technical excellence, and business collaboration across every node of the textile machinery value chain.
                        </p>
                    </div>
                    <div data-testid="mission-block" className="bg-[#0F172A] text-white p-10">
                        <div className="font-mono text-xs text-[#D4AF37] mb-3">02 · OUR MISSION</div>
                        <ul className="space-y-3">
                            {MISSION.map((m, i) => (
                                <li key={m} className="flex gap-3 text-sm leading-relaxed">
                                    <span className="text-[#D4AF37] font-mono shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-white/85">{m}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* WHY JOIN */}
            <section data-testid="why-join-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                <div className="grid md:grid-cols-12 gap-10 mb-14">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Why Join STMF?</div>
                        <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#0F172A] leading-tight">Six reasons to become a member.</h2>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                    {WHY_JOIN.map(({ icon: Icon, title, desc }, i) => (
                        <div key={title} data-testid={`why-join-${i}`} className="bg-white p-8 hover:bg-[#F8F9FA] transition-colors">
                            <div className="w-11 h-11 bg-[#0F172A] text-[#D4AF37] flex items-center justify-center mb-5"><Icon className="w-5 h-5" /></div>
                            <h3 className="font-display text-xl font-bold text-[#0F172A] mb-2">{title}</h3>
                            <p className="text-sm text-[#64748B] leading-relaxed">{desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CATEGORIES */}
            <section data-testid="categories-section" className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="grid md:grid-cols-12 gap-10 mb-14">
                        <div className="md:col-span-5">
                            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Membership Categories</div>
                            <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#0F172A] leading-tight">Five pillars of the textile machinery ecosystem.</h2>
                        </div>
                        <div className="md:col-span-7 md:pt-4">
                            <p className="text-[#64748B] leading-relaxed">From hydrodynamic looms to last-mile technical services — STMF organises the entire value chain into specialised circles, each with dedicated committees, trade forums and certification programs.</p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                        {CATEGORIES.map((c, idx) => (
                            <div key={c.name} data-testid={`category-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`} className={`bg-white p-8 hover:bg-[#F8F9FA] transition-colors ${idx === 0 ? "md:col-span-2" : ""}`}>
                                <div className="font-mono text-[10px] text-[#D4AF37] mb-4">0{idx + 1}</div>
                                <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-2">{c.name}</h3>
                                <p className="text-sm text-[#64748B] mb-6">{c.desc}</p>
                                <div className="text-xs uppercase tracking-[0.12em] text-[#0F172A] font-semibold">{c.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0F172A] text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Ready to join?</div>
                        <h2 className="font-display text-3xl lg:text-5xl font-bold leading-tight">Apply today. Strengthen the federation. Grow your business.</h2>
                    </div>
                    <div className="md:col-span-5 flex md:justify-end">
                        <Link to="/apply" data-testid="cta-strip-apply" className="inline-flex items-center gap-3 px-8 py-5 bg-[#D4AF37] text-[#0F172A] text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#B4952D] transition-colors">Begin Application <span>→</span></Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
